import { useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Copy, Share2, Download, Smartphone, Check } from "lucide-react";
import { toast } from "sonner";

export default function ShareCard() {
  const canvasRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/tenant`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "MyHome at RojasProperty", text: "Access your home — rent, maintenance, and incident reports.", url });
      } catch { /* user cancelled */ }
    } else {
      copyLink();
    }
  };

  const downloadQR = () => {
    const wrapper = canvasRef.current;
    const canvas = wrapper?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = "myhome-rojasproperty.png";
    link.href = canvas.toDataURL("image/png");
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <Card className="overflow-hidden border-slate-200" data-testid="share-card">
      <div className="grid grid-cols-1 md:grid-cols-5">
        {/* QR */}
        <div className="md:col-span-2 bg-gradient-to-br from-emerald-50 via-white to-slate-50 p-6 flex flex-col items-center justify-center border-b md:border-b-0 md:border-r border-slate-100">
          <div ref={canvasRef} className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm" data-testid="qr-code">
            <QRCodeCanvas
              value={url}
              size={156}
              level="M"
              includeMargin={false}
              fgColor="#0F172A"
              imageSettings={{
                src: "/logo.jpg",
                height: 32,
                width: 32,
                excavate: true,
              }}
            />
          </div>
          <p className="text-xs text-slate-500 mt-3 text-center max-w-[180px] leading-relaxed">Scan with your phone camera to open</p>
        </div>

        {/* Instructions */}
        <div className="md:col-span-3 p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-md bg-emerald-50 flex items-center justify-center shrink-0">
              <Smartphone className="h-5 w-5 text-emerald-700" strokeWidth={1.75} />
            </div>
            <div>
              <h3 className="font-display font-semibold">Save MyHome to your phone</h3>
              <p className="text-xs text-slate-500 mt-0.5">Add a one-tap shortcut so rent, maintenance & incident reports are always one tap away.</p>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-1">Your home link</div>
            <div className="font-mono text-xs text-slate-700 break-all" data-testid="share-url">{url}</div>
          </div>

          <ol className="text-xs text-slate-600 space-y-1.5 list-decimal pl-4">
            <li><strong>iPhone (Safari):</strong> tap the Share icon, then "Add to Home Screen".</li>
            <li><strong>Android (Chrome):</strong> tap the three-dot menu, then "Install app" or "Add to Home screen".</li>
          </ol>

          <div className="flex flex-wrap gap-2 pt-1">
            <Button data-testid="copy-link-button" onClick={copyLink} variant="outline" size="sm" className="gap-1.5">
              {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? "Copied" : "Copy link"}
            </Button>
            <Button data-testid="share-button" onClick={shareNative} variant="outline" size="sm" className="gap-1.5">
              <Share2 className="h-3.5 w-3.5" /> Share
            </Button>
            <Button data-testid="download-qr-button" onClick={downloadQR} variant="outline" size="sm" className="gap-1.5">
              <Download className="h-3.5 w-3.5" /> Download QR
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
